import { useState, useEffect, useCallback } from "react";
import {
  Card,
  Box,
  Stack,
  Typography,
  Switch,
  Button,
  Chip,
  Skeleton,
  Alert,
  Snackbar,
} from "@mui/material";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import {
  getAdminUserPayinGateways,
  setAdminUserPayinGateways,
} from "../api";

/**
 * Admin control: enable/disable individual payin providers (Paytm, Cashfree,
 * SLPE) for THIS user, on top of the global on/off. A blocked provider is
 * hidden from the user's Payins screen and rejected server-side on initiate.
 */
export default function UserPayinGatewaysCard({ userId }) {
  const [rows, setRows] = useState([]); // [{ provider, name, enabled, globallyDisabled }]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [snack, setSnack] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    getAdminUserPayinGateways(userId)
      .then(({ data }) => {
        /* Collapse to one row per provider (SLPE returns sub-gateways). */
        const byProvider = {};
        (data.gateways || []).forEach((g) => {
          const key = String(g.provider).toLowerCase();
          if (!byProvider[key]) {
            byProvider[key] = {
              provider: key,
              name:
                key === "slpe"
                  ? "SLPE"
                  : g.name || key.charAt(0).toUpperCase() + key.slice(1),
              enabled: g.enabled !== false,
              globallyDisabled: !!g.globallyDisabled,
            };
          } else {
            // if any sub-gateway is enabled, the provider is enabled
            byProvider[key].enabled =
              byProvider[key].enabled || g.enabled !== false;
          }
        });
        setRows(Object.values(byProvider));
      })
      .catch((e) =>
        setError(e.response?.data?.error || "Failed to load payin gateways"),
      )
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (provider) =>
    setRows((prev) =>
      prev.map((r) =>
        r.provider === provider ? { ...r, enabled: !r.enabled } : r,
      ),
    );

  const save = async () => {
    setSaving(true);
    try {
      const disabled = rows.filter((r) => !r.enabled).map((r) => r.provider);
      await setAdminUserPayinGateways(userId, disabled);
      setSnack({ severity: "success", msg: "Payin gateways updated" });
    } catch (e) {
      setSnack({
        severity: "error",
        msg: e.response?.data?.error || "Failed to save",
      });
    }
    setSaving(false);
  };

  return (
    <Card sx={{ p: 3, borderRadius: 3 }}>
      <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
        <CreditCardRoundedIcon color="primary" fontSize="small" />
        <Typography variant="h6" fontWeight={700}>
          Payin Gateways
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Turn a payment method off for this user. Off = hidden from their Payins
        screen and blocked on payment.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        [...Array(3)].map((_, i) => (
          <Skeleton key={i} height={40} sx={{ mb: 1 }} />
        ))
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No payin gateways configured.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {rows.map((r) => (
            <Box
              key={r.provider}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 0.5,
              }}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography fontWeight={600}>{r.name}</Typography>
                {r.globallyDisabled && (
                  <Chip
                    size="small"
                    label="Off for everyone"
                    variant="outlined"
                    sx={{ fontSize: 10, height: 20 }}
                  />
                )}
              </Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography
                  variant="caption"
                  color={
                    r.globallyDisabled
                      ? "text.disabled"
                      : r.enabled
                        ? "success.main"
                        : "error.main"
                  }
                  fontWeight={700}
                >
                  {r.globallyDisabled
                    ? "Off (global)"
                    : r.enabled
                      ? "Enabled"
                      : "Blocked"}
                </Typography>
                <Switch
                  checked={r.enabled && !r.globallyDisabled}
                  disabled={r.globallyDisabled}
                  onChange={() => toggle(r.provider)}
                />
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || loading}
          sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 700 }}
        >
          Save
        </Button>
      </Box>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} sx={{ borderRadius: 2 }}>
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Card>
  );
}
